import { StarIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

const STAR_COUNT = 5;

// Stable keys so the rows don't need array-index keys.
const STAR_KEYS = ['1', '2', '3', '4', '5'] as const;

type CsatStarsProps = {
  /** Score on a 0–5 scale. Clamped into range. */
  score: number;
  /** Tailwind text-colour class for the filled stars (e.g. `text-chart-1`). */
  colorClassName: string;
  className?: string;
};

/**
 * Five-star CSAT display with fractional fill.
 *
 * A muted base row sits under a coloured copy clipped to `score / 5` of the
 * width, so 3.8 renders as three full stars plus four fifths of the fourth
 * rather than rounding to a whole star. Decorative: every caller renders the
 * numeric score as text beside it, so the stars are hidden from assistive tech.
 */
export const CsatStars = ({ score, colorClassName, className }: CsatStarsProps) => {
  const clampedScore = Math.min(STAR_COUNT, Math.max(0, score));
  const fillPercent = (clampedScore / STAR_COUNT) * 100;

  return (
    <div className={cn('relative inline-flex shrink-0', className)} aria-hidden>
      <div className="flex">
        {STAR_KEYS.map((key) => (
          <StarIcon key={key} className="size-6 shrink-0 fill-bg-muted text-bg-muted" />
        ))}
      </div>
      <div
        className="absolute inset-y-0 left-0 flex overflow-hidden"
        style={{ width: `${fillPercent}%` }}
      >
        {STAR_KEYS.map((key) => (
          <StarIcon key={key} className={cn('size-6 shrink-0 fill-current', colorClassName)} />
        ))}
      </div>
    </div>
  );
};
